/**
***************************************************************************
* NAME 			: MarketShareSummary
* PROPS	 		: -- (array)
* RETURNS 		: Display for the home card on the dashboard
* LAST MODIFIED 	: June 2, 2026
***************************************************************************
*/

import { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import AuthContext from '../../../context/auth-context';
import NotificationContext from '../../../context/notification-context';
import useHttp from '../../../hooks/use-http';
import { getEnrollment } from '../../../api/market-share';
import { PieChart } from '@mui/x-charts/PieChart';
import { useDrawingArea } from '@mui/x-charts';
import { styled } from '@mui/material/styles';
import LoadingSpinner from '../../../components/UI/LoadingSpinner';

const MarketShareSummary = () => {
	const authCtx = useContext(AuthContext);
	const notifyCtx = useContext(NotificationContext);

	const [providerName, setProviderName] = useState("");

	const [enrollment, setEnrollment] = useState([]);
	const [isEnrollmentLoading, setIsEnrollmentLoading] = useState(true);

	const StyledText = styled('text')(({ theme, fontSize = 16 }) => ({
		fill: theme.palette.text.primary,
		textAnchor: 'middle',
		dominantBaseline: 'central',
		fontSize,
	}));

	function PieCenterLabel({ line1, line2 }) {
		const { width, height, left, top } = useDrawingArea();
		return (
			<>
				<StyledText x={left + width / 2} y={top + height / 2 - 12}>
					{line1}
				</StyledText>
				<StyledText fontSize={12} x={left + width / 2} y={top + height / 2 + 12}>
					{line2}
				</StyledText>
			</>
		);
	}

	const { sendRequest: sendEnrollmentRequest, status: enrollmentStatus, data: enrollmentData, error: enrollmentError } = useHttp(getEnrollment);

	/**
	**********
	INIT
	**********
	*/
	useEffect(() => {
		if (enrollmentStatus === 'completed') {
			if (enrollmentError	!== null) {
				notifyCtx.displayNotification(
					"ERROR",
					enrollmentError
				)
			} else if (!enrollmentData['success']) {
				notifyCtx.displayNotification(
					"ERROR",
					enrollmentData['status_code'] + ': ' + enrollmentData['status']
				)
			} else {
				console.log('ENROLLMENT DATA');
				console.log(enrollmentData['data']);

				setProviderName(enrollmentData['data']['provider']);
				setEnrollment(enrollmentData['data']);
			}

			setIsEnrollmentLoading(false);
		}
	}, [enrollmentStatus, enrollmentError, enrollmentData]);

	useEffect(() => {
		// setIsLoading(true);

		const requestData = {
			token: authCtx.token,
			dashboard: authCtx.dashboard
		};

		sendEnrollmentRequest(requestData);
	}, [sendEnrollmentRequest, authCtx.dashboard]);

	return (
		<article>
			<>
				<div className="article_image">
					{ (isEnrollmentLoading) && <LoadingSpinner /> }

					{ (!isEnrollmentLoading) &&
						<div className="ms_columns">
							{/* LEFT COLUMN: month, divider, chart */}
							<div className="ms_col_chart">
								<p><b>{enrollment.month}</b></p>
								<div className="ms_chart_wrap">
									<PieChart
										series={[
											{
												data: enrollment.pieChart.map((provider, i) => ({
													id: i,
													value: parseFloat(provider['marketShare']),
													label: provider['provider'],
													color: provider['color']
												})),
												innerRadius: 85,
												valueFormatter: (item) => `${item.value.toFixed(2)}%`
											},
										]}
										width={300}
										height={250}
										slotProps={{
											legend: { hidden: true }
										}}
									>
										<PieCenterLabel
											line1 = {parseFloat(enrollment.marketShare).toFixed(2) + '%'}
											line2 = {
												(providerName === "Baylor Scott & White Health Plan")
													? "Baylor Scott & White"
													: (providerName === "Blue Cross Blue Shield of Massachusetts")
														? "BCBSMA"
														: providerName
											}
										/>
									</PieChart>
								</div>
							</div>

							{/* RIGHT COLUMN: table headers, divider, data */}
							<div className="ms_col_table">
								<table cellSpacing="0">
									<thead>
										<tr>
											<th scope="col"><span className="visuallyhidden">Provider</span></th>
											<th scope="col">{enrollment.monthAbbr}</th>
											<th scope="col">YoY</th>
											<th scope="col"><span className="visuallyhidden">Trend</span></th>
										</tr>
									</thead>
									<tbody>
										{
											enrollment.dataGrid.map((provider, i) =>
												<tr key={i} style={provider['provider'] === providerName ? { backgroundColor: '#f1f1f1' } : {}}>
													<td>{provider['provider'] === providerName ? <b>{provider['provider']}</b> : provider['provider']}</td>
													<td>{isNaN(parseFloat(provider['marketShare'])) ? 'N/A' : parseFloat(provider['marketShare']).toFixed(2) + '%'}</td>
													<td>
														{(() => {
															const value = parseFloat(provider['yoyChange']);
															if (isNaN(value)) return 'N/A';
															/*
															const displayValue = value < 0 ? Math.abs(value) : value;
															return displayValue.toFixed(2) + '%';
															*/
															return value.toFixed(2) + '%';
														})()}
													</td>
													<td className="ms_trend">
														{!isNaN(parseFloat(provider['yoyChange'])) && provider['yoyChange'] > 0 && <img src="/img/market-share/insight-trend-up-gry.svg" alt="Trending up" width="20" height="20" />}
														{!isNaN(parseFloat(provider['yoyChange'])) && provider['yoyChange'] < 0 && <img src="/img/market-share/insight-trend-dwn-gry.svg" alt="Trending down" width="20" height="20" />}
													</td>
												</tr>
											)
										}
									</tbody>
								</table>
							</div>
						</div>
					}
				</div>
				<div className="article_copy">
					<p><Link to="/market-share/">View details</Link></p>
				</div>
			</>
		</article>
  	)
}

export default MarketShareSummary;